/**
 * Управление темой. Применяет атрибут `data-theme` на <html>,
 * синхронизируется с localStorage. Подписка через простой store.
 */
import { useEffect, useState } from 'react';
import { persistent, type ThemeId } from './storage.js';

export interface ThemeMeta {
  id: ThemeId;
  name: string;
  hint: string;
  swatch: string; // основной цвет для превью
  bg: string;     // цвет фона для превью
  scheme: 'light' | 'dark';
}

export const THEMES: ThemeMeta[] = [
  { id: 'warm', name: 'Ночная',  hint: 'Midnight Brass · тёмная',          swatch: '#F36E21', bg: '#0E0D0B', scheme: 'dark'  },
  { id: 'cool', name: 'Ледяная', hint: 'Slate Studio · светлая, холодная', swatch: '#F36E21', bg: '#F1F5F9', scheme: 'light' },
];

/**
 * Циклический перебор тем по тапу: warm → cool → warm.
 * Возвращает новую активную тему.
 */
export function toggleTheme(): ThemeId {
  const current = persistent.getTheme();
  const idx = THEMES.findIndex((t) => t.id === current);
  const next = THEMES[(idx + 1) % THEMES.length]!.id;
  applyTheme(next);
  return next;
}

const listeners = new Set<(t: ThemeId) => void>();

export function applyTheme(theme: ThemeId): void {
  // ─── Защита от «поэтапной» анимации цветов на смену темы ───
  // CSS-переменные меняются мгновенно, но у многих элементов стоят
  // `transition: background 0.15s, color 0.15s, ...` — и каждый такой
  // элемент плавно интерполирует цвета свою порцию миллисекунд. На сцене
  // выглядит как «сначала кнопка поменялась, потом карточка, потом фон».
  //
  // Решение: вешаем на <html> класс, который через `* { transition: none }`
  // глушит все переходы; меняем data-theme; на следующем кадре снимаем класс.
  // Так браузер делает одну repaint вместо 30 разъезжающихся.
  const html = document.documentElement;
  html.classList.add('jb-theme-switching');
  html.setAttribute('data-theme', theme);

  // theme-color для status bar в PWA
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) {
    const t = THEMES.find((x) => x.id === theme);
    if (t) meta.content = t.bg;
  }
  persistent.setTheme(theme);
  listeners.forEach((fn) => fn(theme));

  // Снять "no-transition" через 2 rAF — первый кадр пройдёт смену цветов,
  // второй гарантирует что браузер уже отрисовал новое состояние.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      html.classList.remove('jb-theme-switching');
    });
  });
}

/** Применить сохранённую тему (вызвать в main.tsx до render). */
export function bootTheme(): ThemeId {
  const t = persistent.getTheme();
  document.documentElement.setAttribute('data-theme', t);
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) {
    const m = THEMES.find((x) => x.id === t);
    if (m) meta.content = m.bg;
  }
  return t;
}

export function useTheme(): [ThemeId, (t: ThemeId) => void] {
  const [theme, setTheme] = useState<ThemeId>(() => persistent.getTheme());
  useEffect(() => {
    const fn = (t: ThemeId) => setTheme(t);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  return [theme, applyTheme];
}
